import React from 'react';
import {View} from 'react-native';
import {isEmpty, map, noop, size} from 'lodash';

import Button from '/component/base/Button';
import Carousel from '/component/base/Carousel';
import Text from '/component/base/Text';
import {useTranslation} from '/provider/TranslationProvider';

import {snapToInterval, styles} from './UpdateNotesAlert.style';

// Define types for the notes structure
interface UpdateNote {
  title: string;
  message: string; // Optional message
  items: string[];
}

// Define types for the component props
interface UpdateNotesAlertProps {
  notes: UpdateNote[]; // Optional so we can use a default value
  onConfirm: () => void;
}

export const UpdateNotesAlert: React.FC<UpdateNotesAlertProps> = ({
  notes = [], // Set default value for notes here
  onConfirm = noop, // Set default value for onConfirm here
}) => {
  const {tt} = useTranslation('updateNotesAlert');

  // The `Carousel` component should take at least 2 child elements.
  // If the count of update notes is less than 2, show only one page of the list.
  const ContentWrapper =
    size(notes) < 2
      ? ({children}: {children: React.ReactNode}) => (
          <View style={styles.contentWrapper}>{children}</View>
        )
      : Carousel;

  return (
    <View style={styles.container}>
      <Text variant="large" style={styles.heading}>
        {tt('whatsNew', 'Whats new')}
      </Text>
      <View style={styles.carouselContainer}>
        <ContentWrapper snapToInterval={snapToInterval}>
          {map(notes, ({title, message, items}: UpdateNote, index: number) => (
            <View key={index} style={styles.itemContainer}>
              <Text variant="heading" style={styles.sectionTitle}>
                {title}
              </Text>
              {message ? (
                <Text style={styles.sectionSubtitle}>{message}</Text>
              ) : null}
              {!isEmpty(items) ? (
                <View style={styles.itemListContainer}>
                  {map(items, (msg: string) => (
                    <Text key={msg} style={styles.sectionListItem}>
                      {`\u2022 ${msg}`}
                    </Text>
                  ))}
                </View>
              ) : null}
            </View>
          ))}
        </ContentWrapper>
      </View>
      <View style={styles.buttonContainer}>
        <Button type="primary" onPress={onConfirm}>
          {tt('gotIt', 'Got it')}
        </Button>
      </View>
    </View>
  );
};

export default UpdateNotesAlert;
